import { Component, inject, signal, ChangeDetectionStrategy, effect } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule, DecimalPipe } from '@angular/common';
import { SubscriptionService, SubscriptionPlan } from '../../services/subscription.service';
import { CheckoutComponent } from '../checkout/checkout.component';
import { AuthService } from '../../services/auth.service';
import { FooterComponent } from '../footer/footer.component';

@Component({
  selector: 'app-subscription-expired-page',
  imports: [CommonModule, DecimalPipe, CheckoutComponent, FooterComponent],
  template: `
    <main>
      <div class="header-container">
        <div class="header-content">
          <h1>ProTyco</h1>
          <p style="font-size: 0.875rem; text-align: center; margin: 0.25rem 0 0 0;">
            <span style="color: oklch(51% 0.28 263.83);">PRO</span>PERTY <span style="color: oklch(51% 0.28 263.83);">TYCO</span>ON
          </p>
        </div>
      </div>

      <div class="expired-scroll">
      <div class="expired-container">
        <div class="expired-card">
          <div class="icon-warning">⚠️</div>
          
          <h1>Ihr Abonnement ist abgelaufen</h1>
          
          <p class="message">
            Um ProTyco weiterhin nutzen zu können, benötigen Sie ein aktives Abonnement.
            Wählen Sie jetzt ein passendes Paket und setzen Sie Ihre Arbeit fort.
          </p>

          @if (loading()) {
            <div class="loading-spinner">Lade verfügbare Pakete...</div>
          } @else {
            <div class="plans-grid">
              @for (plan of plans(); track plan.planKey) {
                <div class="plan-card" [class.popular]="plan.isPopular" (click)="selectPlan(plan)">
                  @if (plan.isPopular) {
                    <div class="popular-badge">Beliebt</div>
                  }
                  
                  <h3>{{ plan.name }}</h3>
                  
                  <div class="price">
                    @if (plan.originalPrice && plan.discountPercent > 0) {
                      <span class="original-price">{{ plan.originalPrice | number:'1.2-2' }}€</span>
                    } @else {
                      <span class="original-price">&nbsp;</span>
                    }
                    <div class="price-duration">
                      <span class="current-price">{{ plan.priceEUR | number:'1.2-2' }}€</span>
                      @if (plan.durationMonths && plan.durationMonths > 0) {
                        <span class="duration">/ {{ plan.durationMonths }} Monat{{ plan.durationMonths > 1 ? 'e' : '' }}</span>
                      }
                    </div>
                  </div>
                  
                  @if (plan.discountPercent !== null && plan.discountPercent !== undefined) {
                    <span class="discount-badge">-{{ plan.discountPercent }}%</span>
                  }
                  
                  @if (plan.description) {
                    <p class="description">{{ plan.description }}</p>
                  }
                  
                  <button class="btn-select">Auswählen</button>
                </div>
              }
            </div>
          }

          <div class="footer-actions">
            <button class="btn-logout" (click)="logout()">
              Abmelden
            </button>
          </div>
        </div>
      </div>
      </div>
    </main>

    @if (showCheckout()) {
      <app-checkout
        [plan]="selectedPlan()!"
        [visible]="showCheckout()"
        (closed)="closeCheckout()"
      ></app-checkout>
    }

    <app-footer></app-footer>
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100vh;
      overflow: hidden;
    }

    main {
      flex: 1;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 0;
      padding: 0 0 1.5rem;
      min-height: 0;
      box-sizing: border-box;
    }

    .expired-scroll {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding: 22px 1.5rem 0;
    }

    .header-container {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      background: white;
      color: white;
      padding: 1rem 2rem 1rem 2rem;
      margin: 0;
      border-radius: 0;
      box-shadow: none;
      border-bottom: 1px solid #d1d5db;
      position: relative;
      z-index: 10;
      overflow: visible;
    }

    .header-container::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
      pointer-events: none;
    }

    .header-content {
      flex: 1;
      text-align: center;
      position: relative;
      overflow: visible;
    }

    .header-content::before {
      content: '';
      position: absolute;
      top: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 40px;
      background: linear-gradient(135deg, oklch(60% 0.3 280) 0%, oklch(45% 0.28 263.83) 100%);
      border-radius: 50%;
      opacity: 0.1;
      filter: blur(20px);
      z-index: 0;
    }

    .header-content h1 {
      background: linear-gradient(135deg, oklch(45% 0.28 263.83) 0%, oklch(60% 0.3 280) 50%, oklch(45% 0.28 263.83) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      font-size: 3rem;
      margin: 0;
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
      font-weight: 900;
      letter-spacing: 0px;
      position: absolute;
      top: -32px;
      left: 50%;
      transform: translateX(calc(-50% + 7px));
      white-space: nowrap;
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
    }

    .header-content p {
      color: oklch(28% 0.06 263.83);
      margin: 43px 0 0 15px !important;
      padding-bottom: 0rem;
      font-weight: 600;
      font-size: 0.75rem;
      letter-spacing: 1px;
      text-transform: uppercase;
      line-height: 1.4;
      user-select: none;
      -webkit-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
    }

    .header-content p span {
      position: relative;
    }

    .expired-container {
      margin-top: 0;
      margin-bottom: 0;
      margin-left: 0;
      margin-right: 0;
      min-height: calc(100vh - 265px);
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      padding: 0px;
    }

    .expired-card {
      background: white;
      border-radius: 16px;
      padding: 22px;
      max-width: 1320px;
      width: 100%;
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
      text-align: center;
    }

    .icon-warning {
      font-size: 2.75rem;
      margin-bottom: 14px;
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.1); }
    }

    h1 {
      font-size: 1.85rem;
      font-weight: 700;
      color: #1e293b;
      margin-bottom: 10px;
    }

    .message {
      font-size: 0.98rem;
      color: #64748b;
      margin-bottom: 28px;
      max-width: 850px;
      margin-left: auto;
      margin-right: auto;
      line-height: 1.4;
    }

    .loading-spinner {
      padding: 24px;
      color: #667eea;
      font-size: 0.98rem;
    }

    .plans-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 14px;
      margin-bottom: 20px;
    }

    .plan-card {
      position: relative;
      background: #f8fafc;
      border: 2px solid #e2e8f0;
      border-radius: 12px;
      padding: 18px 14px;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .plan-card:hover {
      transform: translateY(-4px);
      box-shadow: 0 12px 24px rgba(102, 126, 234, 0.15);
      border-color: #667eea;
    }

    .plan-card.popular {
      border-color: #667eea;
      background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%);
    }

    .popular-badge {
      position: absolute;
      top: -8px;
      right: 16px;
      background: #667eea;
      color: white;
      padding: 2px 10px;
      border-radius: 12px;
      font-size: 0.75rem;
      font-weight: 600;
    }

    .plan-card h3 {
      font-size: 1.15rem;
      font-weight: 700;
      color: #1e293b;
      margin-bottom: 5px;
    }

    .duration {
      color: #64748b;
      font-size: 0.77rem;
      margin-bottom: 10px;
      margin-top: 8px;
    }

    .price {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 5px;
      margin-bottom: 2px;
    }

    .price-duration {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
    }

    .original-price {
      text-decoration: line-through;
      color: #94a3b8;
      font-size: 0.87rem;
    }

    .current-price {
      font-size: 1.85rem;
      font-weight: 700;
      color: #667eea;
    }

    .duration {
      color: #64748b;
      font-size: 0.77rem;
      font-weight: 500;
      margin: 0;
    }

    .discount-badge {
      background: #10b981;
      color: white;
      padding: 2px 6px;
      border-radius: 6px;
      font-size: 0.7rem;
      font-weight: 600;
    }

    .description {
      color: #64748b;
      font-size: 0.77rem;
      margin-bottom: 14px;
      min-height: 26px;
      line-height: 1.3;
    }

    .btn-select {
      width: 100%;
      padding: 9px 14px;
      background: #667eea;
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 0.92rem;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.2s ease;
    }

    .btn-select:hover {
      background: #5a67d8;
    }

    .footer-actions {
      display: flex;
      justify-content: center;
      gap: 8px;
      padding-top: 12px;
      border-top: 1px solid #e2e8f0;
    }

    .btn-logout {
      padding: 8px 16px;
      background: #ef4444;
      color: white;
      border: none;
      border-radius: 8px;
      font-size: 0.9rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
    }

    .btn-logout:hover {
      background: #dc2626;
    }

    ::ng-deep app-footer {
      margin-top: 0 !important;
    }

    ::ng-deep app-footer .footer-content {
      margin-left: -10px;
    }

    @media (max-width: 768px) {
      .header-content h1 {
        font-size: 2rem;
        padding-top: 1rem;
      }

      .header-content p {
        font-size: 0.5625rem !important;
        margin: 32px 0 0 15px !important;
      }

      .expired-card {
        padding: 32px 24px;
      }

      h1 {
        font-size: 1.875rem;
      }

      .plans-grid {
        grid-template-columns: 1fr;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubscriptionExpiredPageComponent {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private subscriptionService = inject(SubscriptionService);
  private authService = inject(AuthService);

  plans = signal<SubscriptionPlan[]>([]);
  loading = signal(false);
  showCheckout = signal(false);
  selectedPlan = signal<SubscriptionPlan | null>(null);

  constructor() {
    this.loadPlans();

    // Effekt um showCheckout Query Parameter zu überwachen
    effect(() => {
      this.activatedRoute.queryParams.subscribe(params => {
        if (params['showCheckout'] === 'true') {
          this.showCheckout.set(true);
        }
      });
    });

    // Effekt um ausgewählten Plan aus planId Query Parameter zu restaurieren
    effect(() => {
      this.activatedRoute.queryParams.subscribe(params => {
        if (params['planId']) {
          const planId = parseInt(params['planId']);
          const loadedPlans = this.plans();
          if (loadedPlans.length > 0) {
            const plan = loadedPlans.find(p => p.planId === planId);
            if (plan) {
              this.selectedPlan.set(plan);
            }
          }
        }
      });
    });
  }

  loadPlans() {
    this.loading.set(true);
    this.subscriptionService.getAvailablePlans().subscribe({
      next: (plans) => {
        // Filter out trial plan
        const filtered = plans.filter(p => p.planKey !== 'trial');
        this.plans.set(filtered);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Fehler beim Laden der Pläne:', error);
        this.loading.set(false);
      }
    });
  }

  selectPlan(plan: SubscriptionPlan) {
    this.selectedPlan.set(plan);
    this.showCheckout.set(true);
    // Setze Query Parameter um Popup-State und Plan-Selection bei Navigation zu erhalten
    this.router.navigate(['/subscription-expired'], { queryParams: { showCheckout: 'true', planId: plan.planId }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  closeCheckout() {
    this.showCheckout.set(false);
    this.selectedPlan.set(null);
    // Entferne Query Parameter um saubere URL zu haben
    this.router.navigate(['/subscription-expired'], { queryParams: {}, replaceUrl: true });
  }

  logout() {
    this.authService.logout();
    this.router.navigate(['/']);
  }
}
